"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.isGatewayLike = exports.Gateway = exports.GatewayClassName = void 0;
const iotv_v3_types_1 = require("@iotv/iotv-v3-types");
const ModelClassInterfaces_1 = require("../../ModelClassInterfaces");
const constants_1 = require("../../constants");
const types_1 = require("../../types");
const millisecondOperations_1 = require("../../util/time/millisecondOperations");
const GeoModelCommon_1 = require("./GeoModelCommon");
exports.GatewayClassName = 'Gateway';
class Gateway extends GeoModelCommon_1.GeoModelCommon {
    constructor(ob) {
        if (ob.type !== undefined && ob.type !== exports.GatewayClassName)
            throw new Error(`Tried to create a ${exports.GatewayClassName} from a ${ob.type} object!`);
        super({ ...ob, type: exports.GatewayClassName });
        this.type = ob.type ?? exports.GatewayClassName;
        this.eui = ob.eui;
        this.networkKey = ob.networkKey;
        this.networkIP = ob.networkIP;
        this.lncInstanceType = ob.lncInstanceType;
        this.regionalPlan = ob.regionalPlan;
        this.state = ob.state;
        this.lastSeen = ob.lastSeen;
        this.lastDeviceSeen = ob.lastDeviceSeen;
        this.manufacturer = ob.manufacturer;
        this.model = ob.model;
        this.serialNumber = ob.serialNumber;
        this.backhaul = ob.backhaul;
        this.AWOLHistory = ob.AWOLHistory;
        this.installDate = ob.installDate;
        this.ports = ob.ports;
        this.lastSendPeriod = ob.lastSendPeriod;
        this.airTimeUtilization = ob.airTimeUtilization;
        this.maxAirTimeUtilization = ob.maxAirTimeUtilization;
        this.congested = ob.congested;
        this.congesting = ob.congesting;
        this.primaryGatewayUplinkCount = ob.primaryGatewayUplinkCount;
        this.secondaryGatewayReceiveCount = ob.secondaryGatewayReceiveCount;
        this.channels = ob.channels;
        this.frequency = ob.frequency;
        this.isAWOL = ob.isAWOL;
        this._permittedAWOL = ob._permittedAWOL;
        this.remoteEnabled = ob.remoteEnabled;
        this.firmwareVersion = ob.firmwareVersion ?? ob.firmwareLevel;
        this.GWUserID = ob.GWUserID;
        this.GWUserPW = ob.GWUserPW;
        this.LastUpdate = ob.LastUpdate;
        this.WAPESSID = ob.WAPESSID;
        this.modemModel = ob.modemModel;
        this.antennaModel = ob.antennaModel;
        this.antennaCable = ob.antennaCable;
        this.modemSerialNumber = ob.modemSerialNumber;
        this.SIMNumber = ob.SIMNumber;
        this.SIMPUK = ob.SIMPUK;
        this.recentDevices = ob.recentDevices;
        this.uplinkCapcity = ob.uplinkCapcity;
        this.downlinkCapacity = ob.downlinkCapacity;
        this.lastAWOL = ob.lastAWOL ?? undefined;
        this.temp = ob.temp;
        this.battery = ob.battery;
        this.batteryLow = ob.batteryLow;
        this.batteryVeryLow = ob.batteryVeryLow;
        this.batteryDead = ob.batteryDead;
        this.WAPDetails = ob.WAPDetails;
        this.WAPEncryption = ob.WAPEncryption;
        this.WAPKey = ob.WAPKey;
        this.WCEncryption = ob.WCEncryption;
        this.WCKey = ob.WCKey;
        this.historyLog = ob.historyLog;
    }
    getStateKeys() {
        let stateAttKeys = ['primaryGatewayUplinkCount', 'secondaryGatewayReceiveCount'];
        return stateAttKeys;
    }
    getUserAttributes() {
        return {
            ...super.getUserAttributes(),
            eui: {
                type: 'String',
                required: true,
                editable: true,
                label: 'EUI',
                name: 'eui',
                validation: { regex: iotv_v3_types_1.CommonRegexMap.LoRa.eui, pass: true, invalidMessage: 'EUI must be 8 hexadecimal bytes' },
                valueFn: (newValue) => (newValue ? newValue.toUpperCase().replace(/\s/g, '') : ''),
                displayFn: (val) => {
                    return val?.replace(/([0-9a-fA-F]{2})(?!=(\s|$))/g, '$1 ')?.toUpperCase() ?? '';
                },
            },
            networkKey: { type: 'Enum', required: true, editable: true, enumKv: iotv_v3_types_1.Networks, label: 'Network' },
            networkIP: { type: 'String', required: false, editable: true, label: 'Network IP' },
            lncInstanceType: {
                type: 'Enum',
                editable: true,
                enumKv: { LOCAL: 'LOCAL', LNC: 'LNC' },
                label: 'LNC / LOCAL',
                default: 'LOCAL',
                dependsOn: (item) => {
                    const partialVKD = item.networkKey === iotv_v3_types_1.Networks.AWS_LPWAN
                        ? {
                            required: item.networkKey === iotv_v3_types_1.Networks.AWS_LPWAN,
                            /** the second part of this uses a side-effect to determine whether the item is new, and that the user should be allowed to edit */
                            editable: !item.lncInstanceType || (item.eui && item.eui !== item.id),
                        }
                        : { hidden: true };
                    return partialVKD;
                },
            },
            regionalPlan: {
                type: 'Enum',
                required: true,
                editable: true,
                enumKv: iotv_v3_types_1.RFRegions,
                label: 'RF Region',
                default: iotv_v3_types_1.RFRegions['AS923-1'],
                dependsOn: (item) => {
                    const partialVKD = item.networkKey === iotv_v3_types_1.Networks.AWS_LPWAN
                        ? {
                            required: item.networkKey === iotv_v3_types_1.Networks.AWS_LPWAN,
                            /** the second part of this uses a side-effect to determine whether the item is new, and that the user should be allowed to edit */
                            editable: !item.regionalPlan || (item.eui && item.eui !== item.id),
                        }
                        : {};
                    return partialVKD;
                },
            },
            state: { type: 'Enum', required: true, editable: true, enumKv: ModelClassInterfaces_1.GatewayState, label: 'State', default: ModelClassInterfaces_1.GatewayState.UNKNOWN },
            lastSeen: {
                type: 'Date',
                required: false,
                editable: false,
                label: 'Last Seen',
                precision: types_1.UserAttributeKeyDatePrecision.DateTimeSecs,
            },
            lastDeviceSeen: { type: 'String', required: false, editable: false, label: 'Last Device Seen' },
            manufacturer: { type: 'String', required: false, editable: true, label: 'Manufacturer' },
            model: { type: 'String', required: false, editable: true, label: 'Model' },
            serialNumber: { type: 'String', required: false, editable: true, label: 'Serial Number' },
            backhaul: {
                type: 'Enum',
                required: false,
                editable: true,
                enumKv: ModelClassInterfaces_1.BackhaulTypes,
                label: 'Backhaul Type',
                default: ModelClassInterfaces_1.BackhaulTypes.Ethernet,
            },
            lastAWOL: {
                type: 'Number',
                required: false,
                editable: false,
                label: 'Last AWOL',
                displayFn: () => this.AWOLHistory && this.AWOLHistory[this.AWOLHistory.length - 1],
            },
            AWOLHistory: {
                type: 'Number',
                required: false,
                editable: false,
                label: 'AWOL History',
                displayFn: () => this.AWOLHistory && [...this.AWOLHistory].reverse().join(', '),
            },
            installDate: {
                type: 'Date',
                required: false,
                editable: true,
                label: 'Install Date',
                displayFn: (actualValue) => (actualValue ? (0, millisecondOperations_1.toDatePickerFormat)(actualValue) : undefined),
                valueFn: (newValue) => (newValue ? new Date(newValue).valueOf() : undefined),
            },
            ports: { type: 'Number', required: false, editable: true, label: 'Ports' },
            lastSendPeriod: { type: 'Number', required: false, editable: false, label: 'Last Send Period' },
            airTimeUtilization: { type: 'Number', required: false, editable: false, units: '%', label: 'Airtime Utilisation' },
            maxAirTimeUtilization: {
                type: 'Number',
                required: false,
                editable: true,
                units: '%',
                label: 'Congestion Limit',
                description: 'Percentage airtime utilized before considered congested',
            },
            congested: { type: 'Boolean', required: false, editable: false, label: 'Is Congested Now' },
            congesting: { type: 'Boolean', required: false, editable: false, label: 'Has shown congestion' },
            primaryGatewayUplinkCount: { type: 'Number', required: false, editable: false, label: 'Primary Receives' },
            secondaryGatewayReceiveCount: { type: 'Number', required: false, editable: false, label: 'Secondary Receives' },
            // primaryDevices: { type: 'Number', editable: false, label: 'Primary Devices', valueFn()},
            channels: { type: 'Number', required: false, editable: true, label: 'Channels' },
            frequency: { type: 'Number', required: false, editable: true, label: 'Frequency' },
            isAWOL: { type: 'Boolean', required: false, editable: false, label: 'Is AWOL?' },
            _permittedAWOL: {
                type: 'Number',
                required: false,
                editable: true,
                label: 'Max AWOL (mins)',
                name: '_permittedAWOL',
                displayFn: (actualValue) => (actualValue ? actualValue / constants_1.Milliseconds.MINUTE : undefined),
                valueFn: (newValue) => (newValue ? newValue * constants_1.Milliseconds.MINUTE : undefined),
            },
            remoteEnabled: { type: 'Boolean', required: false, editable: true, label: 'Remote Enabled' },
            firmwareVersion: { type: 'String', required: false, editable: true, label: 'Firmware Version' },
            GWUserID: { type: 'String', required: false, editable: true, label: 'User ID' },
            GWUserPW: { type: 'String', required: false, editable: true, label: 'Password' },
            LastUpdate: { type: 'String', required: false, editable: true, label: 'Last Updated' },
            WAPESSID: { type: 'String', required: false, editable: true, label: 'WAP ESSID' },
            modemModel: { type: 'String', required: false, editable: true, label: 'Modem Model' },
            antennaModel: { type: 'String', required: false, editable: true, label: 'Antenna Model' },
            antennaCable: { type: 'String', required: false, editable: true, label: 'Antenna Cable' },
            modemSerialNumber: { type: 'String', required: false, editable: true, label: 'Modem Serial Number' },
            SIMNumber: { type: 'String', required: false, editable: true, label: 'SIM Number' },
            SIMPUK: { type: 'String', required: false, editable: true, label: 'SIM PUK' },
            recentDevices: {
                type: 'String',
                required: false,
                editable: false,
                label: 'Recent Devices',
                displayFn: () => this.recentDevices && [...this.recentDevices].reverse().join(', '),
                valueFn: () => this.recentDevices && [...this.recentDevices].reverse().join(', '),
            },
            uplinkCapcity: { type: 'Number', required: false, editable: true, label: 'Uplink Capcity' },
            downlinkCapacity: { type: 'Number', required: false, editable: true, label: 'Downlink Capcity' },
        };
    }
    set permittedAWOL_MS(awolMs) {
        this._permittedAWOL = awolMs;
    }
    get permittedAWOL_MS() {
        return this._permittedAWOL;
    }
}
exports.Gateway = Gateway;
const isGatewayLike = (ob) => {
    return (0, iotv_v3_types_1.isValidBusinessObject)(ob) && ob.type === exports.GatewayClassName;
};
exports.isGatewayLike = isGatewayLike;
